import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';

/**
 * ConfirmDialog - a small, reusable in-app confirmation modal. Replaces native
 * `window.confirm()` for actions that only need a yes/no (no typed name or reason),
 * so the prompt is a real, styled, testable overlay rather than a browser alert
 * (see also ProjectDangerDialog / DeleteDialog for the typed-confirmation variants).
 *
 * When `severity` is set the message renders inside an MUI Alert (e.g. a "warning"
 * cascade notice); otherwise it renders as plain dialog text.
 *
 * @component
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  severity = null,
  testId,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="sm"
    aria-labelledby="confirm-dialog-title"
    aria-describedby="confirm-dialog-description"
    data-testid={testId}
  >
    <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
    <DialogContent>
      {severity ? (
        <Alert severity={severity} sx={{ mb: 0 }} id="confirm-dialog-description">
          {message}
        </Alert>
      ) : (
        <DialogContentText id="confirm-dialog-description">{message}</DialogContentText>
      )}
    </DialogContent>
    <DialogActions>
      {/* #793/#149: focus starts on CANCEL, not on the confirm button. This dialog is
          used for destructive actions (`confirmColor="error"`), and autoFocus on the
          confirm button meant the dialog opened with "Delete" focused — so the Enter
          that dismissed the previous thing destroyed the next one. Focusing the safe
          default is the accessible behaviour AND the safe one. No autoFocus at all:
          MUI's Dialog already moves focus into the dialog on open, Cancel is first in
          DOM order, and an explicit autoFocus anywhere here would trip jsx-a11y's
          no-autofocus for a rule-correct reason — the accessible pattern is to let the
          dialog manage focus, not to hard-code it. */}
      <Button onClick={onClose} color="primary">
        {cancelLabel}
      </Button>
      <Button onClick={onConfirm} color={confirmColor} variant="contained">
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.node.isRequired,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  confirmColor: PropTypes.string,
  severity: PropTypes.string,
  testId: PropTypes.string,
};

export default ConfirmDialog;
